import { computePosition, flip, shift } from "@floating-ui/dom";
import { Editor, posToDOMRect, ReactRenderer } from "@tiptap/react";
import { SuggestionOptions } from "@tiptap/suggestion";

import { SuggestionItem } from "./filter";
import {
  SuggestionsMenu,
  SuggestionsMenuHandle,
  SuggestionsMenuVariant,
} from "./SuggestionsMenu";

const updatePosition = (editor: Editor, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to,
      ),
  };

  computePosition(virtualElement, element, {
    placement: "bottom-start",
    strategy: "absolute",
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = "max-content";
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};

export const renderMenu = (
  variant?: SuggestionsMenuVariant,
): ReturnType<NonNullable<SuggestionOptions["render"]>> => {
  let component: ReactRenderer<
    SuggestionsMenuHandle,
    { items: SuggestionItem[]; variant?: SuggestionsMenuVariant }
  >;
  let removeMouseDownListener: (() => void) | null = null;
  let destroyed = false;

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    removeMouseDownListener?.();
    component.element.remove();
    component.destroy();
  };

  return {
    onStart: (props) => {
      destroyed = false;

      component = new ReactRenderer(SuggestionsMenu, {
        props: { ...props, variant },
        editor: props.editor,
      });

      if (!props.clientRect) {
        return;
      }

      component.element.style.position = "absolute";
      component.element.style.zIndex = "1500"; // TODO: use theme.zIndex.tooltip

      document.body.appendChild(component.element);

      updatePosition(props.editor, component.element);

      const handleMouseDown = (event: MouseEvent) => {
        const target = event.target as Node;

        // Make sure we close the menu when clicking outside of the menu or the editor
        if (
          component.element.contains(target) ||
          props.editor.view.dom.contains(target)
        ) {
          return;
        }

        destroy();
      };

      document.addEventListener("mousedown", handleMouseDown);
      removeMouseDownListener = () => {
        document.removeEventListener("mousedown", handleMouseDown);
        removeMouseDownListener = null;
      };
    },

    onUpdate(props) {
      component.updateProps({ ...props, variant });

      if (!props.clientRect) {
        return;
      }

      updatePosition(props.editor, component.element);
    },

    onKeyDown(props) {
      if (props.event.key === "Escape") {
        destroy();

        return true;
      }

      if (props.event.key === "ArrowDown") {
        component.ref?.focusOnFirstItem?.();
        return true;
      }

      return false; // MenuList handles keyboard navigation
    },

    onExit() {
      destroy();
    },
  };
};
